import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, IconButton, Typography } from "@mui/joy";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface AndroidSmartBannerProps {
  /**
   * When true, the banner stays hidden until the user scrolls past the offset.
   */
  showOnScroll?: boolean;
  /**
   * Scroll distance in pixels required before showing the banner.
   */
  scrollOffset?: number;
  /**
   * Optional selector for a scrollable container. Defaults to window scrolling.
   */
  scrollContainerSelector?: string;
}

export function AndroidSmartBanner({
  showOnScroll = false,
  scrollOffset = 120,
  scrollContainerSelector,
}: AndroidSmartBannerProps) {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(!showOnScroll);

  useEffect(() => {
    // Check if the user is on Android
    const isAndroid = /Android/i.test(navigator.userAgent);
    // Check if user hasn't dismissed the banner before
    const hasNotDismissed = !localStorage.getItem("androidBannerDismissed");

    setIsVisible(isAndroid && hasNotDismissed);
  }, []);

  useEffect(() => {
    if (!showOnScroll) return;

    let ticking = false;

    const scrollTarget: HTMLElement | Window =
      (scrollContainerSelector &&
        (document.querySelector(
          scrollContainerSelector,
        ) as HTMLElement | null)) ||
      window;

    const evaluateScroll = () => {
      const scrollTop =
        scrollTarget === window
          ? window.scrollY || document.documentElement.scrollTop || 0
          : (scrollTarget as HTMLElement).scrollTop || 0;
      const scrolledEnough = scrollTop >= scrollOffset;

      setHasScrolled((prev) =>
        prev !== scrolledEnough ? scrolledEnough : prev,
      );
    };

    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        evaluateScroll();
        ticking = false;
      });
    };

    evaluateScroll();
    scrollTarget.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      scrollTarget.removeEventListener("scroll", handleScroll);
    };
  }, [scrollContainerSelector, scrollOffset, showOnScroll]);

  const handleDismiss = () => {
    setIsVisible(false);
    localStorage.setItem("androidBannerDismissed", "true");
  };

  if (!isVisible || (showOnScroll && !hasScrolled)) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1100,
        bgcolor: "background.surface",
        borderBottom: "1px solid",
        borderColor: "divider",
        px: 2,
        py: 1.5,
        display: { xs: "flex", md: "none" },
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <img
          src={
            import.meta.env.DEV
              ? "/favicon.ico"
              : `${import.meta.env.BASE_URL}/favicon.ico`
          }
          alt="App Icon"
          style={{ width: 40, height: 40, borderRadius: 8 }}
        />
        <Box>
          <Typography level="title-sm">PovChat AI</Typography>
          <Typography level="body-sm">{t("androidBanner.subtitle")}</Typography>
        </Box>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Button
          size="sm"
          color="primary"
          onClick={() => {
            // Replace with your actual Play Store URL
            window.open(
              "https://play.google.com/store/apps/details?id=ai.povchat.characters",
              "_blank",
              "noopener,noreferrer",
            );
          }}
        >
          {t("androidBanner.install")}
        </Button>
        <IconButton
          size="sm"
          variant="plain"
          color="neutral"
          onClick={handleDismiss}
          aria-label="Close Android app banner"
        >
          <CloseIcon />
        </IconButton>
      </Box>
    </Box>
  );
}
